import React, { useState } from 'react';
import {
	Typography,
	Button,
	Autocomplete,
	AutocompleteChangeReason,
	AutocompleteChangeDetails,
	TextField,
	Box,
} from '@mui/material';
import BarCodeReader from 'components/BarCodeReader';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'services/localization';
import { initial, isNumber } from 'lodash';

const findDuplicatesByIndex = (arr: string[]) =>
	arr
		.reduce(
			(acc: (string | number)[], val: string, idx: number) =>
				acc.includes(val) ? acc.concat(idx) : acc.concat(val),
			[],
		)
		.filter(isNumber);

interface Props {
	shopId: string;
	ids: string[];
	onIdsChange: (ids: string[]) => void;
	existingInventoryIds: string[] | undefined;
	autoFocus?: boolean;
	showClearBtn?: boolean;
	showInvalidIdsHelper?: boolean;
	currentItemIds?: string[];
	minOneIdRequired?: boolean;
}

const InventoryIdentifierInput = (props: Props) => {
	const { t } = useTranslation();
	const allIds = props.ids;
	const { existingInventoryIds } = props;
	const { currentItemIds } = props;

	const [newIdValue, setNewIdValue] = useState<string>('');
	const invalidIds = findDuplicatesByIndex(allIds);

	const allIdsCount = allIds.length;
	const invalidIdsCount =
		allIds.filter((id) => existingInventoryIds?.includes(id) && !currentItemIds?.includes(id))
			.length + invalidIds.length;

	const showIdRequiredWarning = props.minOneIdRequired && allIdsCount - invalidIdsCount <= 0;

	const handleIdAdd = async (value: string) => {
		const newIds = [
			...new Set(
				value
					.replace(/\n/g, ',')
					.split(/[ ,\n]+/)
					.filter(Boolean)
					.map((id) => id.toUpperCase()),
			),
		];

		props.onIdsChange([...allIds, ...newIds]);
	};

	const deleteLastId = () => {
		const newIds = initial(allIds);
		props.onIdsChange(newIds);
	};

	const handleClear = () => {
		props.onIdsChange([]);
	};

	const deleteIdAtIndex = (index: number) => {
		const newIds = allIds.filter((id, idx) => idx !== index);
		props.onIdsChange(newIds);
	};

	const handleCustomIdChange = (
		event: React.SyntheticEvent,
		value: string[],
		reason: AutocompleteChangeReason,
		details?: AutocompleteChangeDetails<string>,
	) => {
		if (reason === 'createOption') {
			const id = details?.option!;
			handleIdAdd(id);
		} else if (reason === 'removeOption') {
			deleteLastId();
		}
	};

	return (
		<BarCodeReader barCodeRead={handleIdAdd}>
			<Autocomplete
				id="customIdInput"
				disabled={!existingInventoryIds}
				multiple
				options={[]}
				disableClearable={true}
				freeSolo
				value={allIds}
				onChange={handleCustomIdChange}
				inputValue={newIdValue}
				onInputChange={(_, value) => setNewIdValue(value)}
				onKeyDown={(e) => {
					if (e.code === 'Space') {
						handleIdAdd(newIdValue);
						setNewIdValue('');
					}
				}}
				onBlur={() => {
					handleIdAdd(newIdValue);
					setNewIdValue('');
				}}
				renderTags={(value: string[], getTagProps) =>
					value.map((option: string, index: number) => (
						<Chip
							label={option}
							color={
								invalidIds.includes(index) ||
								(existingInventoryIds?.includes(option) && !currentItemIds?.includes(option))
									? 'error'
									: 'default'
							}
							{...getTagProps({ index })}
							onDelete={(value) => deleteIdAtIndex(index)}
						/>
					))
				}
				renderInput={(params) => (
					<TextField {...params} label={t('inventory.addArticles.identifiers', 'Identifiers')} />
				)}
			/>
			{showIdRequiredWarning && (
				<Typography variant="caption" color="error">
					{t(
						'inventory.individualItemIdRequired',
						'At least one identifier is required for an individual article',
					)}
				</Typography>
			)}
			{allIdsCount > 0 && props.showClearBtn && (
				<Button variant="text" color="primary" onClick={handleClear}>
					<Typography>{t('common:actions.clearAll', 'Clear all')}</Typography>
				</Button>
			)}
			{!!invalidIdsCount && props.showInvalidIdsHelper && (
				<Box mt={1}>
					<Typography variant={'body2'} color="error">
						{t('inventory.invalidProductCodes', 'Invalid product codes')}: {invalidIdsCount}{' '}
						{t('common:pcs', 'pcs')}
					</Typography>
					<Typography variant="caption" color="secondary">
						{t(
							'inventory.productCodesAlreadyFound',
							`These codes are duplicates or already found in your inventory and will not be added`,
						)}
					</Typography>
				</Box>
			)}
			{!existingInventoryIds && (
				<Typography variant={'body1'} color="error">
					{t(
						'inventory.articleIdEditingDisabled',
						'Identifier editing disabled, please try again later.',
					)}
				</Typography>
			)}
		</BarCodeReader>
	);
};

export default InventoryIdentifierInput;
